import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormHelperText, Grid, IconButton, Tooltip, Icon } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { UserContext } from "App";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AddNewAlbum, uploadAlbumMedia } from "APIs/AlbumAPIs";
import FullScreenLoader from "components/Custom/FullScreenLoader";
import { notify } from "App";
// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";
import FormField from "layouts/receipt/components/FormField";

// Smaller inner padding for the cards, so the content gets more width.
// (only affects the cards inside this wrapper, the shared SectionCard is not changed)
const compactCardsSx = { "& > div": { padding: { xs: "12px", md: "16px" } } };

// Back button: maroon (theme primary) outline and text, arrow icon on the left
const backButtonSx = ({ palette }) => ({
  color: palette.primary.main,
  borderColor: palette.primary.main,
  backgroundColor: "transparent",
  borderRadius: "10px",
  boxShadow: "none",
  boxSizing: "border-box",
  height: { xs: "2.25rem", md: "2.5rem" },
  minHeight: { xs: "2.25rem", md: "2.5rem" },
  px: 2.5,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  gap: 0.75,
  transition: "background-color .3s ease, color .3s ease, border-color .3s ease",
  // Hover: gold (theme secondary), same as the other outlined buttons
  "&:hover": {
    color: palette.secondary.main,
    borderColor: palette.secondary.main,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "add-album-form";

const TITLE_ERROR = "Title is required";
const MEDIA_ERROR = "Select at least one image";

// Thumbnail frame. `isNew` = image selected but not uploaded yet (gold border).
const tileSx =
  (isNew = false) =>
  ({ palette, borders }) => ({
    position: "relative",
    display: "block",
    width: "100%",
    height: 140,
    overflow: "hidden",
    textDecoration: "none",
    borderRadius: borders.borderRadius.lg,
    backgroundColor: palette.light.main,
    border: isNew
      ? `2px solid ${palette.secondary.main}`
      : `${borders.borderWidth[1]} solid ${borders.borderColor}`,
  });

// Image with an "open in new tab" icon and a round "x" button in the corner
const ImageTile = ({ src, href, alt, isNew, onRemove }) => (
  <MDBox sx={tileSx(isNew)}>
    <img
      src={src}
      alt={alt}
      style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
    />
    <MDBox
      sx={{
        position: "absolute",
        top: 6,
        right: 6,
        display: "flex",
        alignItems: "center",
        gap: "6px",
      }}
    >
      <Tooltip title="Open in new tab">
        <IconButton
          size="small"
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.65)", color: "#ffffff", padding: "4px" }}
        >
          <OpenInNewIcon style={{ fontSize: "14px" }} />
        </IconButton>
      </Tooltip>
      <MDBox
        component="button"
        type="button"
        onClick={onRemove}
        aria-label="Remove image"
        sx={({ palette }) => ({
          width: 26,
          height: 26,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: 14,
          fontWeight: 700,
          color: palette.white.main,
          backgroundColor: palette.error.main,
          transition: "background-color .3s ease",
          "&:hover": { backgroundColor: palette.primary.main },
        })}
      >
        ✕
      </MDBox>
    </MDBox>
  </MDBox>
);

ImageTile.propTypes = {
  src: PropTypes.string.isRequired,
  href: PropTypes.string,
  alt: PropTypes.string,
  isNew: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
};

ImageTile.defaultProps = {
  href: "",
  alt: "album_img",
  isNew: false,
};

// Native file input (same accept list and behaviour as before), themed
const UploadInput = ({ onChange, error }) => (
  <MDBox
    component="input"
    type="file"
    name="image"
    multiple
    accept=".png,.jpg,.jpeg,.gif,.webp,.tiff,.psd,.bmp,.ico"
    onChange={onChange}
    sx={({ palette, borders }) => ({
      width: "100%",
      padding: "10px",
      cursor: "pointer",
      fontSize: 14,
      color: palette.text.main,
      border: `1px dashed ${error ? palette.error.main : palette.secondary.main}`,
      borderRadius: borders.borderRadius.lg,
      "&::file-selector-button": {
        marginRight: "12px",
        padding: "6px 14px",
        border: "none",
        borderRadius: borders.borderRadius.md,
        cursor: "pointer",
        color: palette.white.main,
        backgroundColor: palette.primary.main,
        transition: "background-color .3s ease",
      },
      "&::file-selector-button:hover": { backgroundColor: palette.secondary.main },
    })}
  />
);

UploadInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

UploadInput.defaultProps = {
  error: false,
};

const AddAlbum = () => {
  const navigate = useNavigate();
  const userData = useContext(UserContext);
  const dispatch = useDispatch();
  const { accentName } = useAccent();

  const [album, setAlbum] = useState({ media_album_title: "" });
  const [newFiles, setNewFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState({});
  const formRef = useRef(null);
  const [scrollToErrorKey, setScrollToErrorKey] = useState(0);

  // After the errors have been rendered, scroll to the first one
  useEffect(() => {
    if (!scrollToErrorKey) return;
    formRef.current
      ?.querySelector(".Mui-error")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [scrollToErrorKey]);

  const handleBlur = (name) => setTouched((prev) => ({ ...prev, [name]: true }));

  // Errors only appear after the field is touched (blurred, or Save was clicked)
  const titleError =
    touched.media_album_title && String(album.media_album_title || "").trim() === ""
      ? TITLE_ERROR
      : "";
  const mediaError = touched.media && newFiles.length === 0 ? MEDIA_ERROR : "";

  const isFormValid = String(album.media_album_title || "").trim() !== "" && newFiles.length > 0;

  const handleAlbumDataChange = (e) => {
    setAlbum((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const newImageObjects = selectedFiles.map((file) => ({
        id: Math.random().toString(36).substring(2, 9),
        file: file,
        preview: URL.createObjectURL(file),
      }));

      setNewFiles((prev) => [...prev, ...newImageObjects]);
      e.target.value = "";
    }
  };

  const handleDeleteNewImage = (fileId) => {
    setNewFiles((prev) => {
      const fileToRemove = prev.find((item) => item.id === fileId);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((item) => item.id !== fileId);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Invalid form: show every error and scroll to the first one
    if (!isFormValid) {
      setTouched({ media_album_title: true, media: true });
      setScrollToErrorKey((key) => key + 1);
      return;
    }

    setLoading(true);
    try {
      const AlbumResponse = await AddNewAlbum(album, userData.access_token, dispatch);
      if (AlbumResponse !== null) {
        if (newFiles.length > 0) {
          let formData = new FormData();
          formData.append("media_album_id", AlbumResponse.media_album_id);
          newFiles.forEach((item) => {
            formData.append("files", item.file);
          });

          await uploadAlbumMedia(formData, userData.access_token, dispatch);
        }
        notify("Album Added!", "success");

        navigate("/album");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <MDBox px={{ xs: 0, md: 1 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />

      {/* Back button sits right beside the page badge (startAction = left of the badge) */}
      <SectionHeader
        icon="library_add"
        label="ADD ALBUM"
        startAction={
          <MDButton
            type="button"
            variant="outlined"
            color="primary"
            sx={backButtonSx}
            onClick={() => navigate("/album")}
          >
            <Icon sx={{ fontSize: "1.1rem !important", fontWeight: "bold" }}>arrow_back</Icon>
            Back
          </MDButton>
        }
      />

      <form ref={formRef} id={FORM_ID} onSubmit={handleSubmit} noValidate autoComplete="off">
        <MDBox sx={compactCardsSx}>
          <SectionCard title="Album" subtitle="Fields marked * are required">
            <Grid container spacing={2}>
              <FormField name="title" required full>
                <MDInput
                  value={album?.media_album_title || ""}
                  name="media_album_title"
                  type="text"
                  placeholder="Enter title"
                  fullWidth
                  onChange={handleAlbumDataChange}
                  onBlur={() => handleBlur("media_album_title")}
                  error={!!titleError}
                />
                {titleError && <FormHelperText error>{titleError}</FormHelperText>}
              </FormField>
            </Grid>
          </SectionCard>

          <SectionCard title="Media" subtitle="Select at least one image for this album">
            <Grid container spacing={2}>
              {/* Image Previews Section */}
              {newFiles.length > 0 && (
                <Grid item xs={12}>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color={accentName}
                    display="block"
                    mb={1.5}
                  >
                    Selected Media Previews ({newFiles.length})
                  </MDTypography>
                  <Grid container spacing={2}>
                    {newFiles.map((item) => (
                      <Grid item key={item.id} xs={6} sm={4} md={3}>
                        <ImageTile
                          isNew
                          src={item.preview}
                          href={item.preview}
                          alt="new_preview"
                          onRemove={() => handleDeleteNewImage(item.id)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}

              {/* Media Upload Input */}
              <FormField name="upload_media" required full>
                <UploadInput error={!!mediaError} onChange={handleFileChange} />
                {mediaError && <FormHelperText error>{mediaError}</FormHelperText>}
              </FormField>
            </Grid>
          </SectionCard>
        </MDBox>
      </form>

      {/* Cancel + Save sit outside the form: bottom right on desktop, centered on mobile.
          Save still submits the form through the `form` attribute. */}
      <MDBox display="flex" gap={1.5} justifyContent={{ xs: "center", md: "flex-end" }}>
        <MDButton
          type="button"
          variant="outlined"
          color="primary"
          sx={ranuOutlinedButtonSx}
          disabled={loading}
          onClick={() => navigate("/album")}
        >
          Cancel
        </MDButton>
        <MDButton color="primary" type="submit" form={FORM_ID} sx={ranuButtonSx} disabled={loading}>
          Save Album
        </MDButton>
      </MDBox>
    </MDBox>
  );
};

export default AddAlbum;
